export interface Translations {
  [key: string]: string
}

export const translations = {
  en: {
    // Header
    appName: 'Kriyate',
    language: 'Language',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    
    // Hero Section
    heroTitle: 'Report Civic Issues in Your Community',
    heroSubtitle: 'Help make your city better by reporting infrastructure problems and civic issues',
    reportIssue: 'Report an Issue',
    viewComplaints: 'View My Complaints',
    publicDashboard: 'Public Dashboard',
    
    // Quick Action Cards
    quickActions: 'Quick Report',
    potholeTitle: 'Road & Pothole Issues',
    potholeDesc: 'Report damaged roads, potholes, or traffic issues',
    garbageTitle: 'Garbage Collection',
    garbageDesc: 'Report missed garbage pickup or waste management issues',
    streetlightTitle: 'Street Light Issues',
    streetlightDesc: 'Report broken or non-functional street lights',
    waterTitle: 'Water Supply Issues',
    waterDesc: 'Report water shortage, contamination, or pipe leaks',
    otherTitle: 'Other Issues',
    otherDesc: 'Report any other civic infrastructure problems',
    
    // Complaint Form
    formTitle: 'Submit New Complaint',
    stepTitle: 'Title & Category',
    stepLocation: 'Location',
    stepDescription: 'Description & Photos',
    stepReview: 'Review & Submit',
    complaintTitle: 'Issue Title',
    titlePlaceholder: 'Brief title describing the issue',
    category: 'Category',
    selectCategory: 'Select issue category',
    description: 'Description',
    descriptionPlaceholder: 'Provide detailed description of the issue',
    uploadPhoto: 'Upload Photo',
    getLocation: 'Get Current Location',
    manualLocation: 'Enter Location Manually',
    locationPlaceholder: 'Enter address or landmark',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit Complaint',
    submitting: 'Submitting...',
    
    // Status
    pending: 'Pending',
    inProgress: 'In Progress',
    resolved: 'Resolved',
    closed: 'Closed',
    rejected: 'Rejected',
    
    // Dashboard
    dashboard: 'Dashboard',
    myComplaints: 'My Complaints',
    totalComplaints: 'Total Complaints',
    pendingCount: 'Pending',
    resolvedCount: 'Resolved',
    filterAll: 'All',
    searchPlaceholder: 'Search complaints...',
    noComplaints: 'No complaints found',
    complaintId: 'Complaint ID',
    submittedOn: 'Submitted on',
    viewDetails: 'View Details',
    
    // Admin
    adminPanel: 'Admin Panel',
    allComplaints: 'All Complaints',
    reports: 'Reports',
    analyticsPage: {
      title: 'Analytics Dashboard',
      subtitle: 'Comprehensive insights and data visualization',
      totalComplaints: 'Total Complaints',
      resolutionRate: 'Resolution Rate',
      avgResolutionTime: 'Avg. Resolution Time',
      pendingComplaints: 'Pending Issues',
      activeUsers: 'Active Citizens',
      days: 'days',
      complaintsByCategory: 'Complaints by Category',
      monthlyTrends: 'Monthly Trends',
      geographicDistribution: 'Geographic Distribution',
      categories: 'Categories',
      intensity: 'Complaint Intensity',
      low: 'Low',
      high: 'High',
      last7Days: 'Last 7 Days',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      lastYear: 'Last Year',
      export: 'Export Report',
      resolvedComplaints: 'Resolved'
    },
    mapView: {
      title: 'Map View',
      subtitle: 'Geographic distribution of civic issues',
      geographicDistribution: 'Geographic Distribution',
      allCategories: 'All Categories',
      export: 'Export Map Data'
    },
    heatmap: 'Heatmap',
    settings: 'Settings',
    assignTo: 'Assign To',
    updateStatus: 'Update Status',
    addComment: 'Add Comment',
    
    // Footer
    disclaimer: 'This is an official government portal for civic issue reporting.',
    helpline: 'Helpline',
    emergency: 'Emergency',
    followUs: 'Follow Us',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    accessibility: 'Accessibility',
    
    // Common
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    retry: 'Retry',

    // Admin Panel
    admin: {
      panel: 'Admin Panel',
      dashboard: 'Dashboard',
      title: 'Administrator',
      staff: 'Staff Member',
      welcomeMessage: 'Welcome to the Government Civic Issues Management System',
      complaints: 'Complaints',
      analytics: 'Analytics',
      users: 'Users',
      map: 'Map View',
      settings: 'Settings',
      totalComplaints: 'Total Complaints',
      pendingComplaints: 'Pending',
      inProgressComplaints: 'In Progress',
      resolvedComplaints: 'Resolved',
      recentComplaints: 'Recent Complaints',
      searchComplaints: 'Search complaints...',
      allStatus: 'All Status',
      allCategories: 'All Categories',
      complaint: 'Complaint',
      status: 'Status',
      priority: 'Priority',
      submitted: 'Submitted',
      submittedBy: 'Submitted by',
      actions: 'Actions',
      noComplaints: 'No complaints found'
    },

    // Priority levels
    priority: {
      low: 'Low',
      medium: 'Medium',
      high: 'High'
    },

    // Status messages
    status: {
      pending: 'Pending',
      inProgress: 'In Progress',
      resolved: 'Resolved',
      in_progress: 'In Progress'
    },

    // Categories
    categories: {
      roads: 'Roads',
      garbage: 'Garbage Collection',
      streetlight: 'Street Lights',
      water: 'Water Supply',
      electricity: 'Electricity'
    },

    // Auth
    auth: {
      logout: 'Logout',
      loginTitle: 'Login to Your Account',
      registerTitle: 'Create New Account',
      citizenLogin: 'Citizen Login',
      officialLogin: 'Official Login',
      loginAsCitizen: 'Login as Citizen',
      loginAsOfficial: 'Login as Official',
      email: 'Email Address',
      mobile: 'Mobile Number',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      emailPlaceholder: 'Enter your email address',
      mobilePlaceholder: 'Enter your mobile number',
      passwordPlaceholder: 'Enter your password',
      confirmPasswordPlaceholder: 'Confirm your password',
      forgotPassword: 'Forgot Password?',
      continueAsGuest: 'Continue as Guest',
      loginButton: 'Login',
      registerButton: 'Register',
      alreadyHaveAccount: 'Already have an account?',
      dontHaveAccount: "Don't have an account?",
      signInHere: 'Sign in here',
      registerHere: 'Register here',
      name: 'Full Name',
      namePlaceholder: 'Enter your full name',
      location: 'Location',
      locationPlaceholder: 'Select your location',
      department: 'Department',
      departmentPlaceholder: 'Select your department',
      govEmailRequired: 'Government email (.gov.in) required',
      terms: 'I agree to the Terms and Conditions',
      privacy: 'I agree to the Privacy Policy',
      loading: 'Please wait...',
      success: 'Success!',
      error: 'Error',
      invalidEmail: 'Please enter a valid email address',
      invalidMobile: 'Please enter a valid mobile number',
      passwordTooShort: 'Password must be at least 8 characters',
      passwordMismatch: 'Passwords do not match',
      requiredField: 'This field is required',
      loginSuccess: 'Login successful! Redirecting...',
      registerSuccess: 'Registration successful! Please login.',
      loginFailed: 'Login failed. Please check your credentials.',
      registerFailed: 'Registration failed. Please try again.',
      selectRole: 'Select Login Type',
      citizenDashboard: 'Citizen Dashboard',
      officialDashboard: 'Official Dashboard'
    }
  },
  
  hi: {
    // Header
    appName: 'Kriyate',
    language: 'भाषा',
    login: 'लॉगिन',
    register: 'पंजीकरण',
    logout: 'लॉगआउट',
    
    // Hero Section
    heroTitle: 'अपने समुदाय में नागरिक समस्याओं की रिपोर्ट करें',
    heroSubtitle: 'बुनियादी ढांचे की समस्याओं और नागरिक मुद्दों की रिपोर्ट करके अपने शहर को बेहतर बनाने में मदद करें',
    reportIssue: 'समस्या की रिपोर्ट करें',
    viewComplaints: 'मेरी शिकायतें देखें',
    publicDashboard: 'सार्वजनिक डैशबोर्ड',
    
    // Quick Action Cards
    quickActions: 'त्वरित रिपोर्ट',
    potholeTitle: 'सड़क और गड्ढे की समस्याएं',
    potholeDesc: 'क्षतिग्रस्त सड़कों, गड्ढों या ट्रैफिक समस्याओं की रिपोर्ट करें',
    garbageTitle: 'कचरा संग्रह',
    garbageDesc: 'छूटे हुए कचरा पिकअप या अपशिष्ट प्रबंधन समस्याओं की रिपोर्ट करें',
    streetlightTitle: 'स्ट्रीट लाइट की समस्याएं',
    streetlightDesc: 'टूटी हुई या गैर-कार्यात्मक स्ट्रीट लाइट्स की रिपोर्ट करें',
    waterTitle: 'पानी की आपूर्ति की समस्याएं',
    waterDesc: 'पानी की कमी, संदूषण या पाइप लीक की रिपोर्ट करें',
    otherTitle: 'अन्य समस्याएं',
    otherDesc: 'किसी भी अन्य नागरिक बुनियादी ढांचे की समस्याओं की रिपोर्ट करें',
    
    // Complaint Form
    formTitle: 'नई शिकायत जमा करें',
    stepTitle: 'शीर्षक और श्रेणी',
    stepLocation: 'स्थान',
    stepDescription: 'विवरण और तस्वीरें',
    stepReview: 'समीक्षा और जमा करें',
    complaintTitle: 'समस्या का शीर्षक',
    titlePlaceholder: 'समस्या का वर्णन करने वाला संक्षिप्त शीर्षक',
    category: 'श्रेणी',
    selectCategory: 'समस्या श्रेणी चुनें',
    description: 'विवरण',
    descriptionPlaceholder: 'समस्या का विस्तृत विवरण प्रदान करें',
    uploadPhoto: 'फोटो अपलोड करें',
    getLocation: 'वर्तमान स्थान प्राप्त करें',
    manualLocation: 'स्थान मैन्युअल रूप से दर्ज करें',
    locationPlaceholder: 'पता या लैंडमार्क दर्ज करें',
    next: 'अगला',
    previous: 'पिछला',
    submit: 'शिकायत जमा करें',
    submitting: 'जमा किया जा रहा है...',
    
    // Status
    pending: 'लंबित',
    inProgress: 'प्रगति में',
    resolved: 'हल हो गया',
    closed: 'बंद',
    rejected: 'अस्वीकृत',
    
    // Dashboard
    dashboard: 'डैशबोर्ड',
    myComplaints: 'मेरी शिकायतें',
    totalComplaints: 'कुल शिकायतें',
    pendingCount: 'लंबित',
    resolvedCount: 'हल हो गया',
    filterAll: 'सभी',
    searchPlaceholder: 'शिकायतें खोजें...',
    noComplaints: 'कोई शिकायत नहीं मिली',
    complaintId: 'शिकायत आईडी',
    submittedOn: 'जमा की गई',
    viewDetails: 'विवरण देखें',
    
    // Admin
    adminPanel: 'एडमिन पैनल',
    allComplaints: 'सभी शिकायतें',
    reports: 'रिपोर्ट्स',
    analytics: 'एनालिटिक्स',
    heatmap: 'हीटमैप',
    settings: 'सेटिंग्स',
    assignTo: 'को सौंपें',
    updateStatus: 'स्थिति अपडेट करें',
    addComment: 'टिप्पणी जोड़ें',
    
    // Footer
    disclaimer: 'यह नागरिक समस्या रिपोर्टिंग के लिए एक आधिकारिक सरकारी पोर्टल है।',
    helpline: 'हेल्पलाइन',
    emergency: 'आपातकाल',
    followUs: 'हमें फॉलो करें',
    privacyPolicy: 'गोपनीयता नीति',
    termsOfService: 'सेवा की शर्तें',
    accessibility: 'पहुंच',
    
    // Common
    close: 'बंद करें',
    cancel: 'रद्द करें',
    save: 'सेव करें',
    edit: 'संपादित करें',
    delete: 'मिटाएं',
    confirm: 'पुष्टि करें',
    loading: 'लोड हो रहा है...',
    error: 'त्रुटि',
    success: 'सफलता',
    retry: 'पुनः प्रयास करें',

    // Admin Panel
    admin: {
      panel: 'एडमिन पैनल',
      dashboard: 'डैशबोर्ड',
      title: 'प्रशासक',
      staff: 'स्टाफ सदस्य',
      welcomeMessage: 'सरकारी नागरिक समस्या प्रबंधन प्रणाली में आपका स्वागत है',
      complaints: 'शिकायतें',
      analytics: 'एनालिटिक्स',
      users: 'उपयोगकर्ता',
      map: 'मैप व्यू',
      settings: 'सेटिंग्स',
      totalComplaints: 'कुल शिकायतें',
      pendingComplaints: 'लंबित',
      inProgressComplaints: 'प्रगति में',
      resolvedComplaints: 'हल हो गया',
      recentComplaints: 'हाल की शिकायतें',
      searchComplaints: 'शिकायतें खोजें...',
      allStatus: 'सभी स्थिति',
      allCategories: 'सभी श्रेणियां',
      complaint: 'शिकायत',
      status: 'स्थिति',
      priority: 'प्राथमिकता',
      submitted: 'जमा किया गया',
      submittedBy: 'द्वारा जमा',
      actions: 'क्रियाएं',
      noComplaints: 'कोई शिकायत नहीं मिली'
    },

    // Priority levels
    priority: {
      low: 'कम',
      medium: 'मध्यम',
      high: 'उच्च'
    },

    // Status messages
    status: {
      pending: 'लंबित',
      inProgress: 'प्रगति में',
      resolved: 'हल हो गया',
      in_progress: 'प्रगति में'
    },

    // Categories
    categories: {
      roads: 'सड़कें',
      garbage: 'कचरा संग्रह',
      streetlight: 'स्ट्रीट लाइट्स',
      water: 'पानी की आपूर्ति',
      electricity: 'बिजली'
    },

    // Auth
    auth: {
      logout: 'लॉगआउट',
      loginTitle: 'अपने खाते में लॉगिन करें',
      registerTitle: 'नया खाता बनाएं',
      citizenLogin: 'नागरिक लॉगिन',
      officialLogin: 'अधिकारी लॉगिन',
      loginAsCitizen: 'नागरिक के रूप में लॉगिन',
      loginAsOfficial: 'अधिकारी के रूप में लॉगिन',
      email: 'ईमेल पता',
      mobile: 'मोबाइल नंबर',
      password: 'पासवर्ड',
      confirmPassword: 'पासवर्ड की पुष्टि करें',
      emailPlaceholder: 'अपना ईमेल पता दर्ज करें',
      mobilePlaceholder: 'अपना मोबाइल नंबर दर्ज करें',
      passwordPlaceholder: 'अपना पासवर्ड दर्ज करें',
      confirmPasswordPlaceholder: 'अपना पासवर्ड की पुष्टि करें',
      forgotPassword: 'पासवर्ड भूल गए?',
      continueAsGuest: 'अतिथि के रूप में जारी रखें',
      loginButton: 'लॉगिन',
      registerButton: 'रजिस्टर',
      alreadyHaveAccount: 'क्या आपका पहले से खाता है?',
      dontHaveAccount: 'क्या आपका खाता नहीं है?',
      signInHere: 'यहाँ साइन इन करें',
      registerHere: 'यहाँ रजिस्टर करें',
      name: 'पूरा नाम',
      namePlaceholder: 'अपना पूरा नाम दर्ज करें',
      location: 'स्थान',
      locationPlaceholder: 'अपना स्थान चुनें',
      department: 'विभाग',
      departmentPlaceholder: 'अपना विभाग चुनें',
      govEmailRequired: 'सरकारी ईमेल (.gov.in) आवश्यक',
      terms: 'मैं नियम और शर्तों से सहमत हूं',
      privacy: 'मैं गोपनीयता नीति से सहमत हूं',
      loading: 'कृपया प्रतीक्षा करें...',
      success: 'सफलता!',
      error: 'त्रुटि',
      invalidEmail: 'कृपया वैध ईमेल पता दर्ज करें',
      invalidMobile: 'कृपया वैध मोबाइल नंबर दर्ज करें',
      passwordTooShort: 'पासवर्ड कम से कम 8 अक्षर का होना चाहिए',
      passwordMismatch: 'पासवर्ड मेल नहीं खाते',
      requiredField: 'यह फील्ड आवश्यक है',
      loginSuccess: 'लॉगिन सफल! रीडायरेक्ट कर रहे हैं...',
      registerSuccess: 'पंजीकरण सफल! कृपया लॉगिन करें।',
      loginFailed: 'लॉगिन असफल। कृपया अपनी जानकारी जांचें।',
      registerFailed: 'पंजीकरण असफल। कृपया पुनः प्रयास करें।',
      selectRole: 'लॉगिन प्रकार चुनें',
      citizenDashboard: 'नागरिक डैशबोर्ड',
      officialDashboard: 'अधिकारी डैशबोर्ड'
    },

    // Analytics Page
    analyticsPage: {
      title: 'एनालिटिक्स डैशबोर्ड',
      subtitle: 'व्यापक अंतर्दृष्टि और डेटा विज़ुअलाइज़ेशन',
      totalComplaints: 'कुल शिकायतें',
      resolutionRate: 'समाधान दर',
      avgResolutionTime: 'औसत समाधान समय',
      pendingComplaints: 'लंबित मुद्दे',
      activeUsers: 'सक्रिय नागरिक',
      days: 'दिन',
      complaintsByCategory: 'श्रेणी के अनुसार शिकायतें',
      monthlyTrends: 'मासिक रुझान',
      geographicDistribution: 'भौगोलिक वितरण',
      categories: 'श्रेणियां',
      intensity: 'शिकायत तीव्रता',
      low: 'कम',
      high: 'उच्च',
      last7Days: 'पिछले 7 दिन',
      last30Days: 'पिछले 30 दिन',
      last90Days: 'पिछले 90 दिन',
      lastYear: 'पिछला साल',
      export: 'रिपोर्ट निर्यात करें',
      resolvedComplaints: 'हल हो गया'
    },
    
    // Map View
    mapView: {
      title: 'मैप व्यू',
      subtitle: 'नागरिक समस्याओं का भौगोलिक वितरण',
      geographicDistribution: 'भौगोलिक वितरण',
      allCategories: 'सभी श्रेणियां',
      export: 'मैप डेटा निर्यात करें'
    }
  }
}

export type Language = keyof typeof translations

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`
}[keyof ObjectType & (string | number)]

export type TranslationKey = NestedKeyOf<typeof translations.en>
